// src/pages/Chatbot/Chatbot.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { useSessionManager } from '../../hooks/useSessionManager';
import { useChatManager } from '../../hooks/useChatManager';
import { useNotifications } from '../../hooks/useNotifications';
import { useWidgetDispatcher } from '../../hooks/useWidgetDispatcher';
import { useFeedback } from '../../hooks/useFeedback';
import useProjects from '../../hooks/useProjects';
import { ChatbotWrapper, themeConfig } from '../../Chatbot.styles';
import { formatText } from '../../utils/formatters';
import { chatbotApi } from '../../Services/chatbotApi';

import FeedbackDrawer from './Chatbot/FeedbackDrawer';
import ToastContainer from '../Atoms/ToastContainer';
import SidebarSystem from './Chatbot/SidebarSystem';
import MainChatArea from './Chatbot/MainChatArea';
import { getOrCreateUserUuid, getOrCreateUserEmail, isAnonymousUser } from '../../utils/userUtils';


import { useAnonymousMessageLimit } from '../../hooks/useAnonymousMessageLimit';
import LoginPromptModal from './Chatbot/LoginPromptModal';
import { useTranslation } from 'react-i18next';

const Chatbot = ({ signout }) => {
    const { t } = useTranslation();
    const token = useSelector((state) => state.token.token);
    const user = useSelector((state) => state.serviceProvider.serviceProvider);
    const { conversationId } = useParams();
    const history = useHistory();
    const location = useLocation();

    const { toasts, showToast, removeToast } = useNotifications();
    const { projects, loading: projectsLoading, error: projectsError, addProject, getProjects } = useProjects(token);

    const { getSessionId, updateSessionId, clearSession, clearAllSessions } = useSessionManager();

    const {
        conversations, messages, setMessages, isLoading, setIsLoading,
        activeConversationId, setActiveConversationId, generateConversationId,
        loadConversations, loadConversationMessages, deleteConversation,
        saveUserMessage, saveBotResponse
    } = useChatManager(showToast, clearSession);

    const {
        feedbackState,
        isSubmitting: isFeedbackSubmitting,
        submitFeedback,
        isMessageRated
    } = useFeedback(showToast);

    const {
        messageCount,
        messageLimit,
        isLimitReached,
        canSendMessage,
        incrementCount,
        resetCount,
        isFeatureEnabled,
        remainingMessages
    } = useAnonymousMessageLimit();

    const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'dark');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [chatMode, setChatMode] = useState('general');
    const [activeProject, setActiveProject] = useState(null);

    const [feedbackDrawer, setFeedbackDrawer] = useState({
        isOpen: false,
        messageId: null,
        currentRating: 0
    });

    const [feedbackEnabled, setFeedbackEnabled] = useState(false);

    const [loginModal, setLoginModal] = useState({
        isOpen: false
    });

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const hasFeedbackParam = queryParams.get('feedback') === 'true';
        //setFeedbackEnabled(hasFeedbackParam);
        setFeedbackEnabled(true);
    }, [location.search]);



    // --- Handlers de Autenticación y Navegación Restaurados ---
    const handleLogin = useCallback(() => {
        showToast('Redirecting to Login...', 'info');
        window.location.href = '/login?redirect=chatbot';
    }, [showToast]);

    const handleModalSignIn = useCallback(() => {
        setLoginModal({ isOpen: false });
        handleLogin();
    }, [handleLogin]);

    const handleModalCreateAccount = useCallback(() => {
        setLoginModal({ isOpen: false });
        showToast('Redirecting to Register...', 'info');
        window.location.href = '/sign-up?redirect=chatbot';
    }, [showToast]);

    const handleModalClose = useCallback(() => {
        setLoginModal({ isOpen: false });
        showToast('You can sign in anytime to continue chatting', 'info');
    }, [showToast]);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('currentUserUuid');
        localStorage.removeItem('currentUserEmail');
        clearAllSessions();
        signout();
        showToast('Logging out...', 'info');
        window.location.reload();
    }, [signout, showToast, clearAllSessions]);

    const handleGoSubscriptions = useCallback(() => {
        history.push('/subscriptions?redirect=chatbot');
    }, [history]);

    // --- Controladores Principales ---
    const startNewChat = useCallback((redirect = true) => {
        // CORRECCIÓN: Solo hacer push si NO estamos ya en /chatbot
        if (redirect && history.location.pathname !== '/chatbot') {
            history.push('/chatbot');
        }
        const newConvId = generateConversationId();
        setActiveConversationId(newConvId);
        getSessionId(newConvId);

        const hasName = !!user?.first_name;

        // const welcomeText = hasName
        //     ? t('chat.welcome_with_name', { name: user.first_name })
        //     : t('chat.welcome_without_name');

        const welcomeMessage = {
            id: crypto.randomUUID(),
            sender: 'bot',
            type: 'welcome',
            // En lugar de 'content', usamos estas propiedades:
            translationKey: hasName ? 'chat.welcome_with_name' : 'chat.welcome_without_name',
            translationParams: hasName ? { name: user.first_name } : undefined
        };

        const actionButtons = `
    <div class="welcome-actions">
        <button class="chat-action-btn btn-primary" data-action="start-creation">Create a new project</button>
        <button class="chat-action-btn btn-ghost" data-action="info-cbrainai">What is CBrainAI?</button>
    </div>
`;

        setMessages([welcomeMessage]);

        setChatMode('general');
        setActiveProject(null);

        if (window.innerWidth <= 768) setIsSidebarOpen(false);

    }, [history, user, generateConversationId, setMessages, setActiveConversationId, getSessionId]);

    const handleProjectSelection = useCallback((project) => {

        if (project) {
            setActiveProject(project);
            //setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: `Context of project "${project.name}" loaded. What would you like to know?` }]);
            showToast('Project selected', 'success');
            //handleSendMessage("talk about my project call "+project.name);
        } else {
            setActiveProject(null);
            setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: `Context changed to 'General'. You can now ask general questions.` }]);
            showToast('General context activated', 'success');
        }
        setChatMode('general');
    }, [setMessages, showToast]);

    useEffect(() => {

        if (activeProject && activeProject.id) {
            const timer = setTimeout(() => {
                handleSendMessage(`Context of project loaded: ${activeProject.name}`);
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [activeProject]);


    const handleDeleteConversation = useCallback(async (timestamp) => {
        const userUuid = localStorage.getItem('currentUserUuid');
        const result = await deleteConversation(userUuid, timestamp);

        // Si se eliminó la conversación activa, redirigir y crear nuevo chat
        if (result?.wasActive) {
            history.push('/chatbot');
            setTimeout(() => {
                startNewChat(false);
            }, 100);
        }
    }, [deleteConversation, history, startNewChat]);

    useEffect(() => {
        const userUuid = localStorage.getItem('currentUserUuid');
        const queryParams = new URLSearchParams(location.search);
        const urlProjectId = queryParams.get('projectId');
        const urlProjectName = queryParams.get('projectName');

        if (urlProjectId && urlProjectName && !conversationId) {
            if (userUuid) {
                loadConversations(userUuid);
                startNewChat(false);
                setTimeout(() => handleProjectSelection({ id: urlProjectId, name: urlProjectName }), 100);
                history.push('/chatbot', null);
            } else {
                history.push('/chatbot');
                startNewChat();
            }
        } else if (conversationId) {
            if (!userUuid) { history.push('/chatbot'); startNewChat(); }
            else { loadConversations(userUuid); }
        } else {
            if (userUuid) loadConversations(userUuid);
            startNewChat();
        }
    }, [conversationId, location.search, user, startNewChat, handleProjectSelection, loadConversations, history]);

    useEffect(() => {
        if (conversationId && conversations.length > 0) {
            const foundConversation = conversations.find(conv => conv.conversation_id === conversationId);

            if (foundConversation && activeConversationId !== conversationId) {
                loadConversationMessages(localStorage.getItem('currentUserUuid'), foundConversation);
            } else if (!foundConversation && conversationId !== activeConversationId) {
                history.push('/chatbot');
                startNewChat();
            }
        }
    }, [conversations, conversationId, history, startNewChat, loadConversationMessages, activeConversationId]);


    const sendQueryToApi = async (messageText, messageId) => {
        setIsLoading(true);
        let convId = activeConversationId || generateConversationId();
        if (!activeConversationId) setActiveConversationId(convId);

        const userUuid = getOrCreateUserUuid();
        const userEmail = getOrCreateUserEmail();
        const isAnonymous = isAnonymousUser();

        const sessionId = getSessionId(convId);

        if (history.location.pathname === '/chatbot' && userUuid && !isAnonymous) {
            history.push({
                pathname: `/chatbot/c/${convId}`,
                search: history.location.search
            });
        }

        const requestStartTime = Date.now();

        console.log(activeProject)
        try {
            const responseData = await chatbotApi.sendMessage({
                message: messageText,
                userEmail,
                userUuid,
                conversationId: convId,
                projectId: activeProject ? activeProject.id : null,
                messageId,
                sessionId: sessionId,
                anonymousSessionId: sessionId
            });

            if (responseData && responseData.response) {
                //const responseTimeMs = Date.now() - requestStartTime;
                if (responseData.sessionId && responseData.sessionId !== sessionId) {
                    console.log('Backend returned new sessionId, updating...', {
                        old: sessionId,
                        new: responseData.sessionId
                    });
                    updateSessionId(convId, responseData.sessionId);
                }

                const responseTimeMs = 0;
                const newMessages = [];
                if (responseData.debug_info) {
                    let debugInfo = `🔍 DEBUG: Usuario: ${responseData.debug_info.user_email} | Membresía: ${responseData.debug_info.membership} | Agente: ${responseData.debug_info.agent}`;
                    if (activeProject) debugInfo += ` | Proyecto: ${activeProject.name}`;
                    newMessages.push({ id: crypto.randomUUID(), sender: 'debug', content: debugInfo });
                }


                newMessages.push({
                    id: crypto.randomUUID(),
                    sender: 'bot',
                    content: responseData.response,
                    widget: responseData.ui_widget,
                    governance: responseData.governance,
                    messageId: messageId,
                });
                setMessages(prev => [...prev, ...newMessages]);

                if (userUuid && messageId) {
                    await saveBotResponse({
                        messageId,
                        botResponse: responseData.response,
                        agentUsed: responseData.debug_info?.agent || 'unknown',
                        responseTimeMs,
                        input_tokens: responseData.debug_info?.tokens?.inputTokens,
                        output_tokens: responseData.debug_info?.tokens?.outputTokens
                    });
                }

                if (userUuid) setTimeout(() => loadConversations(userUuid), 1000);
            } else {
                setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: 'Lo siento, hubo un problema al procesar tu solicitud.', isError: true }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: 'Error al conectar con el asistente. Por favor, intenta de nuevo.', isError: true }]);
        } finally {
            setIsLoading(false);
        }
    };

    const { dispatchWidgetAction } = useWidgetDispatcher({
        setMessages, addProject, setActiveProject, showToast, sendQueryToApi, setIsLoading
    });

    const handleSendMessage = async (messageText) => {
        if (!messageText.trim()) return;
        if (!canSendMessage()) {
            setLoginModal({ isOpen: true });
            return;
        }


        const userUuid = localStorage.getItem('currentUserUuid') ?? crypto.randomUUID();
        const userEmail = localStorage.getItem('currentUserEmail') ?? crypto.randomUUID() + '@nomail.com';
        const messageId = crypto.randomUUID();

        setMessages(prev => [...prev, { id: messageId, sender: 'user', content: messageText }]);

        if (isFeatureEnabled && isAnonymousUser()) {
            incrementCount();
        }

        if (userUuid && userEmail) {
            await saveUserMessage({
                messageId,
                conversationId: activeConversationId || generateConversationId(),
                userUuid,
                userEmail,
                userMessage: messageText,
                projectId: activeProject?.id || null,
                subscriptionPlan: user?.subscription?.plan || 'unknown',
                subscriptionStatus: user?.subscription?.status || 'Anonymous'
            });
        }

        await sendQueryToApi(messageText, messageId);
    };

    const handleActionClick = async (action, data) => {
        if (action === 'start-creation') {
            setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.widget?.action === 'local_create_project') return prev;

                return [...prev, {
                    id: crypto.randomUUID(), // ID Universal
                    sender: 'bot',
                    content: 'Perfect! Complete the information to create your new project:',
                    widget: {
                        action: 'local_create_project', // Este match debe existir en el Dispatcher
                        type: 'form',
                        completed: false,
                        required_fields: [ // Ajustado a la nomenclatura de tu MessageList
                            { id: 'name', label: 'Project Name', type: 'text', required: true },
                            { id: 'description', label: 'Description', type: 'text', required: true },
                            { id: 'zip_code', label: 'Zip Code', type: 'text', required: true },
                            { id: 'gross_area', label: 'Gross Area (sqft)', type: 'number', required: true },
                            { id: 'work_type', label: 'Work Type', type: 'text', required: true },
                            { id: 'main_building_use', label: 'Main Building Use', type: 'text', required: true }
                        ]
                    }
                }];
            });
        }

        if (action === 'start-selection') {
            setChatMode('selecting_project'); setIsLoading(true);
            setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: 'Buscando tus proyectos existentes...' }]);
            try {
                const projs = await getProjects();
                if (projs && projs.length > 0) {
                    const projectButtons = projs.map(proj => `<button class="chat-action-btn" data-action="select-project-${proj.id}" data-name="${proj.name}">${proj.name}</button>`).join('');
                    setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: formatText(`These are your projects. Which one do you want to activate?`) + `<br> ${projectButtons}` }]);
                } else {
                    setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: formatText('I didn\'t find any existing projects. Do you want to') + ' <button class="chat-action-btn" data-action="start-creation">create a new one</button>?' }]);
                }
            } catch (error) {
                showToast('Error loading projects', 'error');
                setMessages(prev => [...prev, { id: crypto.randomUUID(), sender: 'bot', content: 'Error loading your projects.', isError: true }]);
            } finally { setIsLoading(false); setChatMode('general'); }
        }
        if (action.startsWith('select-project-')) {
            const projectId = action.substring('select-project-'.length);
            handleProjectSelection({ id: projectId, name: data.name || `Project ${projectId}` });
        }




    };

    const handleRatingClick = useCallback((messageId, rating) => {
        setFeedbackDrawer({
            isOpen: true,
            messageId,
            currentRating: rating
        });
    }, []);

    const handleFeedbackSubmit = useCallback(async (messageId, rating, comment) => {
        const success = await submitFeedback(messageId, rating, comment);
        if (success) {
            setFeedbackDrawer({ isOpen: false, messageId: null, currentRating: 0 });
        }
    }, [submitFeedback]);

    const toggleTheme = useCallback(() => {
        const newTheme = themeMode === 'dark' ? 'light' : 'dark';
        setThemeMode(newTheme); localStorage.setItem('themeMode', newTheme);
    }, [themeMode]);

    const handleConversationClick = useCallback((conversation) => {
        const userUuid = localStorage.getItem('currentUserUuid');

        if (userUuid) {

            history.push({
                pathname: `/chatbot/c/${conversation.conversation_id}`,
                search: history.location.search
            });

            loadConversationMessages(userUuid, conversation);
        }
    }, [history, loadConversationMessages]);

    return (
        <ChatbotWrapper theme={themeConfig[themeMode]}>

            <ToastContainer toasts={toasts} removeToast={removeToast} />

            <SidebarSystem
                isOpen={isSidebarOpen}
                onToggle={setIsSidebarOpen}
                themeMode={themeMode}
                toggleTheme={toggleTheme}
                startNewChat={startNewChat}
                conversations={conversations}
                activeConversationId={activeConversationId}
                loadConversation={handleConversationClick}
                deleteConversation={handleDeleteConversation}
                user={user}
                token={token}
                handleLogout={handleLogout}
                handleLogin={handleLogin}
                handleGoSubscriptions={handleGoSubscriptions}
            />

            <MainChatArea
                activeProject={activeProject}
                projects={projects}
                projectsLoading={projectsLoading}
                projectsError={projectsError}
                handleProjectSelection={handleProjectSelection}
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={setIsSidebarOpen}
                messages={messages}
                isLoading={isLoading}
                handleActionClick={handleActionClick}
                dispatchWidgetAction={dispatchWidgetAction}
                feedbackEnabled={feedbackEnabled}
                handleRatingClick={handleRatingClick}
                feedbackState={feedbackState}
                handleSendMessage={handleSendMessage}
            />

            <FeedbackDrawer
                isOpen={feedbackDrawer.isOpen}
                messageId={feedbackDrawer.messageId}
                currentRating={feedbackDrawer.currentRating}
                onClose={() => setFeedbackDrawer({ isOpen: false, messageId: null, currentRating: 0 })}
                onSubmit={handleFeedbackSubmit}
            />

            <LoginPromptModal
                isOpen={loginModal.isOpen}
                messageCount={messageCount}
                messageLimit={messageLimit}
                onSignIn={handleModalSignIn}
                onCreateAccount={handleModalCreateAccount}
                onClose={handleModalClose}
            />
        </ChatbotWrapper>
    );
};

export default Chatbot;